import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useUser } from '../context/UserContext';

interface ValidationResult {
  test: string;
  status: 'passed' | 'warning' | 'failed';
  message: string;
  details?: any;
}

interface CollectionStats {
  name: string;
  count: number;
  sampleDoc?: any;
}

export default function IntegrityValidator() {
  const { user } = useUser();
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<ValidationResult[]>([]);
  const [collectionStats, setCollectionStats] = useState<CollectionStats[]>([]);

  // Solo admins pueden acceder
  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Acceso Denegado</h4>
          <p>Solo administradores pueden ejecutar la validación de integridad.</p>
        </div>
      </div>
    );
  }

  const addResult = (test: string, status: 'passed' | 'warning' | 'failed', message: string, details?: any) => {
    setResults(prev => [...prev, { test, status, message, details }]);
  };

  const runValidation = async () => {
    setIsRunning(true);
    setResults([]);
    setCollectionStats([]);

    try {
      // 1. Estadísticas básicas de colecciones
      await validateCollectionStats();
      
      // 2. Validar IDs únicos
      await validateUniqueIds();
      
      // 3. Validar relaciones agencias-miembros
      await validateAgencyMemberRelations();
      
      // 4. Validar emails duplicados
      await validateUniqueEmails();
      
      // 5. Validar estructura de datos
      await validateDataStructure();
      
      // 6. Validar relaciones propiedades (si existen)
      await validatePropertyRelations();
      
    } catch (error: any) {
      addResult('Sistema', 'failed', `Error crítico en validación: ${error.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  const validateCollectionStats = async () => {
    const collections = ['admin', 'asociado', 'asesor', 'inmobiliaria', 'propiedades'];
    const stats: CollectionStats[] = [];
    
    for (const collectionName of collections) {
      try {
        const snapshot = await getDocs(collection(db, collectionName));
        const docs = snapshot.docs;
        
        stats.push({
          name: collectionName,
          count: docs.length,
          sampleDoc: docs.length > 0 ? { id: docs[0].id, ...docs[0].data() } : null
        });
        
        addResult(
          `Colección ${collectionName}`,
          docs.length > 0 ? 'passed' : 'warning',
          `${docs.length} documentos encontrados`
        );
        
      } catch (error: any) {
        addResult(`Colección ${collectionName}`, 'failed', `Error accediendo: ${error.message}`);
      }
    }
    
    setCollectionStats(stats);
  };

  const validateUniqueIds = async () => {
    const collections = [
      { name: 'inmobiliaria', idField: 'inmobiliaria_id' },
      { name: 'asociado', idField: 'asociado_id' },
      { name: 'asesor', idField: 'asesor_id' },
      { name: 'propiedades', idField: 'propiedad_id' }
    ];

    for (const { name, idField } of collections) {
      try {
        const snapshot = await getDocs(collection(db, name));
        const ids: string[] = [];
        const duplicates: string[] = [];

        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const id = data[idField];
          if (id) {
            if (ids.includes(id)) {
              duplicates.push(id);
            } else {
              ids.push(id);
            }
          }
        });

        if (duplicates.length === 0) {
          addResult(
            `IDs únicos - ${name}`,
            'passed',
            `Todos los ${idField} son únicos (${ids.length} documentos)`
          );
        } else {
          addResult(
            `IDs únicos - ${name}`,
            'failed',
            `IDs duplicados encontrados: ${duplicates.join(', ')}`,
            duplicates
          );
        }

      } catch (error: any) {
        addResult(`IDs únicos - ${name}`, 'failed', `Error validando: ${error.message}`);
      }
    }
  };

  const validateAgencyMemberRelations = async () => {
    try {
      // Obtener todas las inmobiliarias
      const inmobiliariasSnapshot = await getDocs(collection(db, 'inmobiliaria'));
      let orphanedMembers = 0;
      let emptyAgencies = 0;
      let validRelations = 0;

      for (const inmobiliariaDoc of inmobiliariasSnapshot.docs) {
        const inmobiliaria = inmobiliariaDoc.data();
        const inmobiliaria_id = inmobiliaria.inmobiliaria_id;
        
        const asesorIds = inmobiliaria.asesor_id || [];
        const asociadoIds = inmobiliaria.asociado_id || [];
        
        if (asesorIds.length === 0 && asociadoIds.length === 0) {
          emptyAgencies++;
        } else {
          validRelations++;
        }

        // Verificar que los asesores existan
        for (const asesorId of asesorIds) {
          const asesorQuery = query(collection(db, 'asesor'), where('asesor_id', '==', asesorId));
          const asesorSnapshot = await getDocs(asesorQuery);
          
          if (asesorSnapshot.empty) {
            addResult(
              'Relaciones rotas',
              'failed',
              `Asesor ${asesorId} referenciado en ${inmobiliaria.nombre} no existe`
            );
          }
        }

        // Verificar que los asociados existan
        for (const asociadoId of asociadoIds) {
          const asociadoQuery = query(collection(db, 'asociado'), where('asociado_id', '==', asociadoId));
          const asociadoSnapshot = await getDocs(asociadoQuery);
          
          if (asociadoSnapshot.empty) {
            addResult(
              'Relaciones rotas',
              'failed',
              `Asociado ${asociadoId} referenciado en ${inmobiliaria.nombre} no existe`
            );
          }
        }
      }

      // Verificar miembros huérfanos (sin inmobiliaria)
      const asesoresSnapshot = await getDocs(collection(db, 'asesor'));
      for (const asesorDoc of asesoresSnapshot.docs) {
        const asesor = asesorDoc.data();
        const inmobiliariaIds = Array.isArray(asesor.inmobiliaria_id) 
          ? asesor.inmobiliaria_id 
          : [asesor.inmobiliaria_id];
        
        for (const inmobiliariaId of inmobiliariaIds) {
          if (inmobiliariaId) {
            const inmobiliariaQuery = query(collection(db, 'inmobiliaria'), where('inmobiliaria_id', '==', inmobiliariaId));
            const inmobiliariaSnapshot = await getDocs(inmobiliariaQuery);
            
            if (inmobiliariaSnapshot.empty) {
              orphanedMembers++;
              addResult(
                'Miembros huérfanos',
                'warning',
                `Asesor ${asesor.nombre} referencia inmobiliaria inexistente: ${inmobiliariaId}`
              );
            }
          }
        }
      }

      addResult(
        'Resumen relaciones',
        validRelations > 0 ? 'passed' : 'warning',
        `${validRelations} agencias con miembros, ${emptyAgencies} agencias vacías, ${orphanedMembers} miembros huérfanos`
      );

    } catch (error: any) {
      addResult('Relaciones agencias-miembros', 'failed', `Error validando: ${error.message}`);
    }
  };

  const validateUniqueEmails = async () => {
    try {
      const collections = ['admin', 'asociado', 'asesor'];
      const emailMap: { [email: string]: string[] } = {};
      
      for (const collectionName of collections) {
        const snapshot = await getDocs(collection(db, collectionName));
        
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const email = data.correo?.toLowerCase().trim();
          
          if (email && email.includes('@')) {
            if (!emailMap[email]) {
              emailMap[email] = [];
            }
            emailMap[email].push(`${collectionName}:${doc.id}`);
          }
        });
      }

      const duplicates = Object.entries(emailMap).filter(([_, docs]) => docs.length > 1);
      
      if (duplicates.length === 0) {
        addResult('Emails únicos', 'passed', 'No se encontraron emails duplicados');
      } else {
        duplicates.forEach(([email, docs]) => {
          addResult(
            'Emails duplicados',
            'warning',
            `Email ${email} usado en: ${docs.join(', ')}`,
            { email, documents: docs }
          );
        });
      }

    } catch (error: any) {
      addResult('Emails únicos', 'failed', `Error validando: ${error.message}`);
    }
  };

  const validateDataStructure = async () => {
    try {
      // Validar que los IDs de negocio existan donde deberían
      const collections = [
        { name: 'inmobiliaria', requiredId: 'inmobiliaria_id' },
        { name: 'asociado', requiredId: 'asociado_id' },
        { name: 'asesor', requiredId: 'asesor_id' },
        { name: 'propiedades', requiredId: 'propiedad_id' }
      ];

      for (const { name, requiredId } of collections) {
        const snapshot = await getDocs(collection(db, name));
        let missingIds = 0;
        let emptyIds = 0;

        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const id = data[requiredId];

          if (!id) {
            missingIds++;
          } else if (typeof id === 'string' && id.trim() === '') {
            emptyIds++;
          }
        });

        if (missingIds === 0 && emptyIds === 0) {
          addResult(
            `Estructura - ${name}`,
            'passed',
            `Todos los documentos tienen ${requiredId} válido`
          );
        } else {
          addResult(
            `Estructura - ${name}`,
            'failed',
            `${missingIds} documentos sin ${requiredId}, ${emptyIds} con ${requiredId} vacío`
          );
        }
      }

    } catch (error: any) {
      addResult('Estructura de datos', 'failed', `Error validando: ${error.message}`);
    }
  };

  const validatePropertyRelations = async () => {
    try {
      const propiedadesSnapshot = await getDocs(collection(db, 'propiedades'));
      
      if (propiedadesSnapshot.empty) {
        addResult('Propiedades', 'warning', 'No hay propiedades para validar');
        return;
      }

      let propiedadesConAgencia = 0;
      let propiedadesConAsesor = 0;
      let relacionesRotas = 0;

      for (const propiedadDoc of propiedadesSnapshot.docs) {
        const propiedad = propiedadDoc.data();
        
        // Verificar relación con agencia
        const inmobiliariaId = propiedad.agencia_info?.inmobiliaria_id;
        if (inmobiliariaId) {
          propiedadesConAgencia++;
          
          const inmobiliariaQuery = query(collection(db, 'inmobiliaria'), where('inmobiliaria_id', '==', inmobiliariaId));
          const inmobiliariaSnapshot = await getDocs(inmobiliariaQuery);
          
          if (inmobiliariaSnapshot.empty) {
            relacionesRotas++;
            addResult(
              'Relaciones propiedades',
              'failed',
              `Propiedad ${propiedad.list_number} referencia agencia inexistente: ${inmobiliariaId}`
            );
          }
        }

        // Verificar relación con asesor
        const asesorIds = propiedad.asesor_id || [];
        if (asesorIds.length > 0) {
          propiedadesConAsesor++;
          
          for (const asesorId of asesorIds) {
            const asesorQuery = query(collection(db, 'asesor'), where('asesor_id', '==', asesorId));
            const asesorSnapshot = await getDocs(asesorQuery);
            
            if (asesorSnapshot.empty) {
              relacionesRotas++;
              addResult(
                'Relaciones propiedades',
                'failed',
                `Propiedad ${propiedad.list_number} referencia asesor inexistente: ${asesorId}`
              );
            }
          }
        }
      }

      addResult(
        'Resumen propiedades',
        relacionesRotas === 0 ? 'passed' : 'warning',
        `${propiedadesSnapshot.size} propiedades, ${propiedadesConAgencia} con agencia, ${propiedadesConAsesor} con asesor, ${relacionesRotas} relaciones rotas`
      );

    } catch (error: any) {
      addResult('Relaciones propiedades', 'failed', `Error validando: ${error.message}`);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'passed': return '✅';
      case 'warning': return '⚠️';
      case 'failed': return '❌';
      default: return '🔍';
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'passed': return 'border-success bg-success bg-opacity-10 text-success';
      case 'warning': return 'border-warning bg-warning bg-opacity-10 text-warning';
      case 'failed': return 'border-danger bg-danger bg-opacity-10 text-danger';
      default: return 'border-info bg-info bg-opacity-10 text-info';
    }
  };

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h3 className="mb-0">🔍 Validador de Integridad AMPI</h3>
              <small>Verificación completa de relaciones y consistencia de datos</small>
            </div>
            <div className="card-body">
              
              {/* Botón de ejecución */}
              <div className="text-center mb-4">
                <button
                  className={`btn btn-lg ${isRunning ? 'btn-warning' : 'btn-primary'}`}
                  onClick={runValidation}
                  disabled={isRunning}
                >
                  {isRunning ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Ejecutando Validación...
                    </>
                  ) : (
                    <>
                      🔍 EJECUTAR VALIDACIÓN DE INTEGRIDAD
                    </>
                  )}
                </button>
              </div>

              {/* Estadísticas de colecciones */}
              {collectionStats.length > 0 && (
                <div className="row mb-4">
                  <div className="col-12">
                    <h5>📊 Estadísticas de Colecciones</h5>
                    <div className="row">
                      {collectionStats.map(stat => (
                        <div key={stat.name} className="col-md-2 mb-2">
                          <div className="card text-center">
                            <div className="card-header bg-info text-white">
                              <h6 className="mb-0">{stat.name}</h6>
                            </div>
                            <div className="card-body">
                              <h4 className={stat.count > 0 ? 'text-success' : 'text-muted'}>
                                {stat.count}
                              </h4>
                              <small>documentos</small>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Resultados de validación */}
              {results.length > 0 && (
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">📋 Resultados de Validación</h5>
                    <div>
                      <span className="badge bg-success me-1">
                        {results.filter(r => r.status === 'passed').length} ✅
                      </span>
                      <span className="badge bg-warning me-1">
                        {results.filter(r => r.status === 'warning').length} ⚠️
                      </span>
                      <span className="badge bg-danger">
                        {results.filter(r => r.status === 'failed').length} ❌
                      </span>
                    </div>
                  </div>
                  <div className="card-body" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    {results.map((result, index) => (
                      <div 
                        key={index} 
                        className={`border-start border-3 p-3 mb-2 ${getStatusClass(result.status)}`}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <h6 className="mb-1">
                              {getStatusIcon(result.status)} {result.test}
                            </h6>
                            <p className="mb-1">{result.message}</p>
                            {result.details && (
                              <details className="mt-2">
                                <summary className="text-muted small" style={{ cursor: 'pointer' }}>
                                  Ver detalles
                                </summary>
                                <pre className="small mt-2 bg-light p-2 rounded">
                                  {JSON.stringify(result.details, null, 2)}
                                </pre>
                              </details>
                            )}
                          </div>
                          <span className={`badge ${
                            result.status === 'passed' ? 'bg-success' :
                            result.status === 'warning' ? 'bg-warning' : 'bg-danger'
                          }`}>
                            {result.status.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}